import { Injectable } from '@angular/core';
import {
    CognitoUserPool,
    AuthenticationDetails,
    CognitoUser,
    CognitoUserSession
} from 'amazon-cognito-identity-js';
import { CognitoIdentityCredentials, config as AWSConfig, AWSError } from 'aws-sdk';
import { environment } from '../../environments/environment';

export class User {
    static default = new User(false);
    constructor(public signedIn: boolean, public username?: string, public userId?: string) {
    }
}

@Injectable({
    providedIn: 'root'
})
export class AwsAuthService {
    public static statusCodes = {
        success: 'success',
        signedIn: 'signedIn',
        signedOut: 'signedOut',
        incompletedSigninData: 'incompletedSigninData',
        newPasswordRequired: 'newPasswordRequired',
        verificationCodeRequired: 'verificationCodeRequired',
        passwordChanged: 'passwordChanged',
        noSuchUser: 'noSuchUser',
        unknownError: 'unknownError'
    };
    private signupData: any = {};
    currentStatus = 'unknown';
    cognitoAwsCredentials: CognitoIdentityCredentials;
    private static userPoolLoginKey = `cognito-idp.${environment.userPool.region}.amazonaws.com/${environment.userPool.UserPoolId}`;
    private userPool = new CognitoUserPool(environment.userPool);

    constructor() {
    }

    private getCognitoUser(username?: string) {
        username = username || this.signupData.username;
        if (!username) {
            return undefined;
        }
        return new CognitoUser({
            Username: username,
            Pool: this.userPool
        });
    }

    authenticate(user: any,
        callback?: (err: Error, statusCode: string) => void) {
        const authService = this;
        const username = user.username || this.signupData.username;
        const password = user.password || this.signupData.password;
        if (!username || !password) {
            callback(new Error('AuthenticationDetails are incomplete.'),
                AwsAuthService.statusCodes.incompletedSigninData);
            return;
        } else {
            this.signupData.username = username;
            this.signupData.password = password;
        }

        const cognitoUser = this.getCognitoUser(username);
        const auth = new AuthenticationDetails({ Username: username, Password: password });
        cognitoUser.authenticateUser(auth, {
            onSuccess: function (authResult) {
                authService.currentStatus = AwsAuthService.statusCodes.signedIn;
                authService.signupData = {};
                callback(null, AwsAuthService.statusCodes.signedIn);
            },

            onFailure: function (err) {
                authService.currentStatus = AwsAuthService.statusCodes.unknownError;
                if (err.code === 'UserNotFoundException' || err.code === 'NotAuthorizedException') {
                    callback(err, AwsAuthService.statusCodes.noSuchUser);
                } else {
                    callback(err, AwsAuthService.statusCodes.unknownError);
                }
            },
            newPasswordRequired: function (userAttributes, requiredAttributes) {
                if (!user.newPassword) {
                    const newNoNewPasswordError = new Error('First time logged in but new password is not provided');
                    if (callback) {
                        authService.currentStatus = AwsAuthService.statusCodes.newPasswordRequired;
                        callback(newNoNewPasswordError, AwsAuthService.statusCodes.newPasswordRequired);
                        return;
                    } else {
                        throw newNoNewPasswordError;
                    }
                }
                if (authService.signupData) {
                    userAttributes = Object.assign(userAttributes, authService.signupData.additionalData);
                }
                delete userAttributes.email_verified;
                cognitoUser.completeNewPasswordChallenge(user.newPassword, userAttributes, this);
            }
        });
    }

    getCurrentUser(callback: (err?: Error, user?: User) => void) {
        this.getCurrentCognitoUser((err, cognitoUser) => {
            if (cognitoUser && cognitoUser.getUsername()) {
                const identityId = this.cognitoAwsCredentials ? this.cognitoAwsCredentials.identityId : undefined;
                callback(undefined, new User(true, cognitoUser.getUsername(), identityId));
            } else {
                callback(undefined, User.default);
            }
        });
    }

    private getCurrentCognitoUser(callback: (err1?: Error, cognitoUser?: CognitoUser) => void) {
        const cognitoUser = this.userPool.getCurrentUser();
        if (cognitoUser) {
            cognitoUser.getSession((err: Error, session: CognitoUserSession) => {
                if (session && session.isValid()) {
                    if (!this.cognitoAwsCredentials || this.cognitoAwsCredentials.needsRefresh()) {
                        this.updateAWSCredentials(session.getIdToken().getJwtToken(), cognitoUser.getUsername(), (err2) => {
                            if (err2) {
                                callback(err2);
                            } else {
                                callback(undefined, cognitoUser);
                            }
                        });
                    } else {
                        callback(undefined, cognitoUser);
                    }
                } else {
                    callback(undefined, undefined);
                }
            });
        } else {
            callback(undefined, undefined);
        }
    }


    private updateAWSCredentials(sessionToken: string, username: string, callback: (err?: Error) => void) {
        const logins = {};
        logins[AwsAuthService.userPoolLoginKey] = sessionToken;
        this.cognitoAwsCredentials = new CognitoIdentityCredentials(
            {
                IdentityPoolId: environment.identityPoolId,
                Logins: logins,
                LoginId: username
            },
            {
                region: environment.userPool.region
            }
        );
        // call refresh method in order to authenticate user and get new temp credentials
        this.cognitoAwsCredentials.refresh((err: AWSError) => {
            if (err) {
                callback(err);
            } else {
                AWSConfig.credentials = this.cognitoAwsCredentials;
                callback(null);
            }
        });
    }

    updateCredentials() {
        const cognitoUser = this.userPool.getCurrentUser();
        if (cognitoUser) {
            cognitoUser.getSession((err: Error, session: CognitoUserSession) => {
                if (session && session.isValid()) {
                    const logins = {};
                    logins[AwsAuthService.userPoolLoginKey] = session.getIdToken().getJwtToken();
                    this.cognitoAwsCredentials = new CognitoIdentityCredentials(
                        {
                            IdentityPoolId: environment.identityPoolId,
                            Logins: logins,
                            LoginId: cognitoUser.getUsername()
                        },
                        {
                            region: environment.userPool.region
                        }
                    );
                    AWSConfig.credentials = this.cognitoAwsCredentials;
                }
            });
        }
    }
}
